import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import Settings from "../common/Settings";
import Avatar from "../common/Avatar";
import logoImage from "../../assets/logo.png";

const HeaderContainer = styled.header`
  background: transparent;
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
  border-bottom: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  z-index: 100;
`;

const HeaderContent = styled.div`
  max-width: var(--max-width-wide);
  margin: 0 auto;
  padding: 1.5rem 2rem;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;

  @media (max-width: 768px) {
    padding: 1rem 1.5rem;
    display: flex;
    justify-content: space-between;
  }
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text);
  opacity: 0.8;
  transition: all 0.2s ease;
  text-decoration: none;

  &:hover {
    color: var(--color-accent);
    opacity: 1;
  }

  svg {
    width: 20px;
    height: 20px;
    fill: currentColor;
  }
`;

const LeftNavLink = styled(Link)`
  font-size: 0.95rem;
  font-weight: 500;
  color: var(--color-text);
  text-decoration: none;
  position: relative;
  opacity: 0.8;

  &:hover {
    color: var(--color-accent);
    opacity: 1;
    text-decoration: none;
  }

  &.active {
    color: var(--color-accent);
    opacity: 1;

    &::after {
      content: "";
      position: absolute;
      bottom: -0.5rem;
      left: 0;
      right: 0;
      height: 2px;
      background: var(--color-accent);
    }
  }
`;

const CenterLogo = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  background: transparent;
  border-radius: 50%;
  padding: 8px;
  box-shadow: none;
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.05);
    box-shadow: none;
    background: transparent;
  }

  @media (max-width: 768px) {
    padding: 6px;
  }
`;

const LogoImage = styled.img`
  height: 48px;
  width: 48px;
  object-fit: contain;

  @media (max-width: 768px) {
    height: 36px;
    width: 36px;
  }
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: 2.5rem;
  justify-content: flex-end;

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  font-size: 0.95rem;
  font-weight: 500;
  color: var(--color-text);
  text-decoration: none;
  position: relative;
  opacity: 0.8;

  &:hover {
    color: var(--color-accent);
    opacity: 1;
    text-decoration: none;
  }

  &.active {
    color: var(--color-accent);
    opacity: 1;

    &::after {
      content: "";
      position: absolute;
      bottom: -0.5rem;
      left: 0;
      right: 0;
      height: 2px;
      background: var(--color-accent);
    }
  }
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
`;

const SettingsButton = styled.button`
  background: none;
  border: none;
  color: var(--color-text);
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.2s ease;
  opacity: 0.8;

  &:hover {
    background: transparent;
    opacity: 1;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const AuthSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const LogoutButton = styled.button`
  background: var(--color-accent);
  color: var(--color-white);
  border: none;
  padding: 0.4rem 1rem;
  border-radius: 6px;
  font-weight: 500;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-accent-hover);
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: var(--color-text);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 6px;
  transition: all 0.2s ease;
  opacity: 0.8;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &:hover {
    background: transparent;
    opacity: 1;
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;

const MobileMenu = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  width: 280px;
  background: var(--color-white);
  transform: translateX(${(props) => (props.isOpen ? "0" : "100%")});
  transition: transform 0.3s ease;
  z-index: 1001;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.1);
  overflow-y: auto;
`;

const MobileMenuHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid var(--color-border);
`;

const MobileMenuTitle = styled.h3`
  font-size: 1.25rem;
  color: var(--color-text);
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--color-text);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 6px;
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-hover-bg);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const MobileNavLinks = styled.div`
  padding: 1rem 0;
`;

const MobileNavLink = styled(Link)`
  display: block;
  padding: 1rem 1.5rem;
  font-size: 1rem;
  font-weight: 500;
  color: var(--color-text);
  text-decoration: none;
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-hover-bg);
    color: var(--color-accent);
  }

  &.active {
    color: var(--color-accent);
    background: var(--color-hover-bg);
  }
`;

const MobileAuthSection = styled.div`
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--color-border);
  margin-top: auto;
`;

const MobileUserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const MobileSettingsButton = styled.button`
  width: 100%;
  background: transparent;
  color: var(--color-text);
  border: 1px solid var(--color-border);
  padding: 0.75rem 1rem;
  border-radius: 6px;
  font-weight: 500;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &:hover {
    background: var(--color-hover-bg);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const MobileLogoutButton = styled.button`
  width: 100%;
  background: var(--color-accent);
  color: var(--color-white);
  border: none;
  padding: 0.75rem 1rem;
  border-radius: 6px;
  font-weight: 500;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-accent-hover);
  }
`;

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 999;
  opacity: ${(props) => (props.isOpen ? 1 : 0)};
  visibility: ${(props) => (props.isOpen ? "visible" : "hidden")};
  transition: opacity 0.3s ease, visibility 0.3s ease;
`;

const SettingsIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <title>Settings</title>
    <circle cx="12" cy="12" r="3"></circle>
    <path d="M12 1v6m0 6v6m4.22-10.22l1.42-1.42m-1.42 10.28l1.42 1.42M20 12h-6m-6 0H2m10.22-4.22L10.8 6.36m1.42 10.28l-1.42 1.42"></path>
  </svg>
);

const HamburgerIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <title>Menu</title>
    <line x1="3" y1="6" x2="21" y2="6"></line>
    <line x1="3" y1="12" x2="21" y2="12"></line>
    <line x1="3" y1="18" x2="21" y2="18"></line>
  </svg>
);

const CloseIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <title>Close</title>
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const LinkedInIcon = () => (
  <svg viewBox="0 0 24 24">
    <title>LinkedIn</title>
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

const Header = () => {
  const location = useLocation();
  const { currentUser, logout } = useAuth();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Prevent body scroll when menus are open
  useEffect(() => {
    if (settingsOpen || mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [settingsOpen, mobileMenuOpen]);

  const handleMobileSettings = () => {
    setMobileMenuOpen(false);
    setSettingsOpen(true);
  };

  // Debug logging for profile photo issue
  useEffect(() => {
    if (currentUser) {
      console.log("Current user data:", {
        email: currentUser.email,
        displayName: currentUser.displayName,
        photoURL: currentUser.photoURL,
        uid: currentUser.uid,
      });
    }
  }, [currentUser]);

  return (
    <>
      <HeaderContainer>
        <HeaderContent>
          <LeftSection>
            <SocialLink
              href="https://www.linkedin.com/in/isabeljadehuang/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
            >
              <LinkedInIcon />
            </SocialLink>
            <LeftNavLink
              to="/about"
              className={location.pathname === "/about" ? "active" : ""}
            >
              About
            </LeftNavLink>
          </LeftSection>

          <CenterLogo to="/">
            <LogoImage src={logoImage} alt="JISDEU Logo" />
          </CenterLogo>

          <Nav>
            <NavLink
              to="/"
              className={location.pathname === "/" ? "active" : ""}
            >
              Monologues
            </NavLink>
            <NavLink
              to="/briefly"
              className={location.pathname === "/briefly" ? "active" : ""}
            >
              Briefly
            </NavLink>

            <RightSection>
              {currentUser && (
                <>
                  <SettingsButton
                    onClick={() => setSettingsOpen(true)}
                    title="Settings"
                    aria-label="Open settings menu"
                  >
                    <SettingsIcon />
                  </SettingsButton>
                  <AuthSection>
                    <UserInfo>
                      <Avatar
                        src={currentUser.photoURL}
                        alt={currentUser.displayName || currentUser.email}
                        size="32px"
                        fontSize="14px"
                        border="2px solid var(--color-border)"
                      />
                    </UserInfo>
                    <LogoutButton onClick={logout}>Logout</LogoutButton>
                  </AuthSection>
                </>
              )}
            </RightSection>
          </Nav>

          <MobileMenuButton
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Open menu"
          >
            <HamburgerIcon />
          </MobileMenuButton>
        </HeaderContent>
      </HeaderContainer>

      {/* Mobile Menu */}
      <MobileMenu isOpen={mobileMenuOpen}>
        <MobileMenuHeader>
          <MobileMenuTitle>Menu</MobileMenuTitle>
          <CloseButton
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Close menu"
          >
            <CloseIcon />
          </CloseButton>
        </MobileMenuHeader>

        <MobileNavLinks>
          <MobileNavLink
            to="/"
            className={location.pathname === "/" ? "active" : ""}
          >
            Monologues
          </MobileNavLink>
          <MobileNavLink
            to="/briefly"
            className={location.pathname === "/briefly" ? "active" : ""}
          >
            Briefly
          </MobileNavLink>
          <MobileNavLink
            to="/about"
            className={location.pathname === "/about" ? "active" : ""}
          >
            About
          </MobileNavLink>
        </MobileNavLinks>

        {currentUser && (
          <MobileAuthSection>
            <MobileUserInfo>
              <Avatar
                src={currentUser.photoURL}
                alt={currentUser.displayName || currentUser.email}
                size="32px"
                fontSize="14px"
                border="2px solid var(--color-border)"
              />
              <span>{currentUser.displayName || currentUser.email}</span>
            </MobileUserInfo>
            <MobileSettingsButton onClick={handleMobileSettings}>
              <SettingsIcon /> Settings
            </MobileSettingsButton>
            <MobileLogoutButton onClick={logout}>Logout</MobileLogoutButton>
          </MobileAuthSection>
        )}
      </MobileMenu>

      <Backdrop
        isOpen={settingsOpen || mobileMenuOpen}
        onClick={() => {
          setSettingsOpen(false);
          setMobileMenuOpen(false);
        }}
      />
      <Settings isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </>
  );
};

export default Header;
